/**
 * Auto-buy walker (Patch BB).
 *
 * Expands AutoBuySchedule[] into a batch of new PurchaseTransaction /
 * EtfTransaction rows for every scheduled date that has passed since the
 * schedule's lastPopulatedDate. The store's runAutoBuyWalker action calls
 * this; the engine itself is pure (no Date.now() or store access except
 * through inputs) so it's deterministic and testable.
 *
 * --- Pricing model ---
 * All backfilled rows use TODAY's price (passed in via priceMap), not the
 * historical price on each scheduled date. Rationale:
 *
 *   - The client app has no scheduled background tasks; the walker only
 *     fires when the user opens the app. To use historical-per-date prices
 *     we'd need either (a) a historical-price API call per backfilled date,
 *     which is heavy on CoinGecko/Yahoo and adds 100s of ms per date, or
 *     (b) a server-side cron writing rows at scheduled times. (b) is a
 *     defensible future upgrade but a big architecture step for v1.
 *
 *   - Rows are flagged `autoCreated: true`; the UI surfaces an AUTO badge.
 *     Any user edit (date / USD / BTC / shares / price / note) clears the
 *     flag via the store's update* actions. So a backfill that misprices a
 *     row is a one-tap fix on review.
 *
 *   - The typical user opens the app frequently (daily), so backfill spans
 *     a few days at most. Today's-price drift over that span is small.
 *
 * --- ETF integer-shares rule ---
 * For ETFs: shares = floor(amountUsd / pricePerShare). If shares is 0
 * (i.e. amount < price/share), the period is SKIPPED entirely. lastPop'd
 * date STILL advances so we don't re-attempt the same period forever.
 * Leftover dollars (amount - shares * price) are discarded per period;
 * not aggregated across periods. (Aggregation adds bookkeeping complexity
 * and the leftover is typically $1-$2 per period, not material.)
 *
 * --- Re-enable / disable semantics ---
 * Walker only emits for schedules with `enabled: true` AND `amountUsd > 0`.
 * If a schedule is disabled, the walker leaves lastPopulatedDate alone;
 * re-enable is handled by the store action (upsertAutoBuy resets
 * lastPopulatedDate to today when toggling enabled from false to true so
 * the user doesn't get surprise-backfilled rows for the disabled gap).
 *
 * --- Missing-price defer ---
 * If priceMap doesn't have an entry for an asset (e.g. ETF live price
 * fetch failed and there's no manual override), the walker skips that
 * schedule entirely and does NOT advance lastPopulatedDate. Next walker
 * run (when prices are available) picks up where we left off.
 *
 * --- Idempotency ---
 * Running the walker twice with the same inputs produces the same output
 * the first time and an empty result the second time. The walker reads
 * lastPopulatedDate and only emits dates strictly after it, so re-runs
 * are a no-op until the next scheduled date arrives.
 */

import { Decimal } from './money.js';
import type {
  AutoBuySchedule,
  PurchaseTransaction,
  EtfTransaction,
} from './types.js';

export interface AutoBuyWalkResult {
  /** New BTC purchases to add. IDs are NOT assigned; the caller assigns. */
  newPurchases: Omit<PurchaseTransaction, 'id'>[];
  /** New ETF transactions to add. IDs are NOT assigned; the caller assigns. */
  newEtfTransactions: Omit<EtfTransaction, 'id'>[];
  /** Schedules whose lastPopulatedDate moved. Apply via state merge. */
  scheduleUpdates: { id: string; lastPopulatedDate: Date }[];
}

const DAY_MS = 24 * 3600_000;
const WEEK_MS = 7 * DAY_MS;
const BIWEEK_MS = 14 * DAY_MS;

/**
 * Today's date as a Date object pinned to noon UTC, where "today" means
 * the caller's LOCAL calendar date - not the UTC calendar date.
 *
 * Why this exists (Patch BD): `new Date()` returns the current moment.
 * After ~8pm EDT (or any local-evening hour west of UTC), that moment is
 * already in the NEXT calendar day in UTC even though the user's local
 * clock still says today. Calling .toISOString() on it then shows
 * tomorrow's date, which propagates the wrong calendar day into:
 *   - date picker default values
 *   - scheduled-date comparisons in the walker
 *   - the date stored on auto-generated rows
 * That was the symptom: enable a schedule on May 10 evening, get a row
 * dated May 11.
 *
 * The noon-UTC convention matches parseIsoDate() in AutoBuyPanel (which
 * uses "T12:00:00Z" for the same reason). Noon UTC is safely the same
 * calendar day in every timezone from UTC-11 to UTC+12, so date
 * arithmetic doesn't slip across day boundaries unexpectedly.
 *
 * Use this anywhere a Date represents a CALENDAR DAY rather than a
 * precise MOMENT IN TIME - auto-buy startDate defaults, walker "today"
 * boundary comparisons, re-enable lastPopulatedDate resets.
 */
export function todayLocalNoonUtc(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0));
}

/** Last day of (year, monthZeroBased), accounting for leap years. */
function daysInMonth(year: number, monthZeroBased: number): number {
  // Date.UTC(year, month + 1, 0) gives the last day of `month` because
  // day-0 of the next month is the last day of the previous month.
  return new Date(Date.UTC(year, monthZeroBased + 1, 0)).getUTCDate();
}

/** Clamp a target DOM to the month's length. Used to handle "day 31 in
 *  February" gracefully: returns 28 (or 29 in a leap year) rather than
 *  letting Date.setUTCDate roll over into March. */
function adjustDayToMonth(year: number, monthZeroBased: number, targetDay: number): number {
  return Math.min(targetDay, daysInMonth(year, monthZeroBased));
}

/**
 * Step forward exactly one cadence period from `from`. Returns null if
 * the cadence is custom-monthly but no customMonthDays were provided
 * (caller should treat as a misconfigured schedule and stop).
 */
function stepForward(
  from: Date,
  cadence: AutoBuySchedule['cadence'],
  customMonthDays: number[] | undefined,
): Date | null {
  if (cadence === 'daily') return new Date(from.getTime() + DAY_MS);
  if (cadence === 'weekly') return new Date(from.getTime() + WEEK_MS);
  if (cadence === 'biweekly') return new Date(from.getTime() + BIWEEK_MS);

  if (cadence === 'monthly') {
    // Preserve day-of-month intent across months. Direct setUTCMonth on
    // a Jan 31 rolls over to March 3 in non-leap years; we instead jump
    // to day 1 of next month, then set the day, clamping to month length.
    const next = new Date(from);
    const originalDom = from.getUTCDate();
    next.setUTCDate(1);
    next.setUTCMonth(next.getUTCMonth() + 1);
    next.setUTCDate(adjustDayToMonth(next.getUTCFullYear(), next.getUTCMonth(), originalDom));
    return next;
  }

  if (cadence === 'custom-monthly') {
    if (!customMonthDays || customMonthDays.length === 0) return null;
    const sorted = [...customMonthDays].sort((a, b) => a - b);
    const fromDom = from.getUTCDate();
    const fromYear = from.getUTCFullYear();
    const fromMonth = from.getUTCMonth();
    // Try to find a later day in the same month.
    const nextSameMonth = sorted.find(d => d > fromDom);
    if (nextSameMonth !== undefined) {
      const next = new Date(from);
      next.setUTCDate(adjustDayToMonth(fromYear, fromMonth, nextSameMonth));
      return next;
    }
    // No later day this month; jump to next month, first day in list.
    const next = new Date(from);
    next.setUTCDate(1);
    next.setUTCMonth(next.getUTCMonth() + 1);
    next.setUTCDate(adjustDayToMonth(next.getUTCFullYear(), next.getUTCMonth(), sorted[0]!));
    return next;
  }

  return null;
}

/**
 * Find the FIRST date >= schedule.startDate that satisfies the schedule's
 * cadence. For daily/weekly/biweekly/monthly the start date itself is
 * always valid. For custom-monthly the first valid date is the earliest
 * customMonthDays entry that's >= startDate's DOM in the start month, OR
 * the smallest customMonthDays entry in the following month.
 */
function firstScheduledDate(schedule: AutoBuySchedule): Date | null {
  if (schedule.cadence !== 'custom-monthly') {
    return new Date(schedule.startDate);
  }
  if (!schedule.customMonthDays || schedule.customMonthDays.length === 0) return null;
  const sorted = [...schedule.customMonthDays].sort((a, b) => a - b);
  const start = new Date(schedule.startDate);
  const startDom = start.getUTCDate();
  const eligibleSameMonth = sorted.find(d => d >= startDom);
  if (eligibleSameMonth !== undefined) {
    const next = new Date(start);
    next.setUTCDate(adjustDayToMonth(next.getUTCFullYear(), next.getUTCMonth(), eligibleSameMonth));
    return next;
  }
  // Jump to first day in list, next month.
  const next = new Date(start);
  next.setUTCDate(1);
  next.setUTCMonth(next.getUTCMonth() + 1);
  next.setUTCDate(adjustDayToMonth(next.getUTCFullYear(), next.getUTCMonth(), sorted[0]!));
  return next;
}

/**
 * Generator yielding every scheduled date for a schedule, starting
 * from (lastPopulatedDate + 1 step) or startDate, and ending at the
 * latest date <= today (and <= endDate if set). Caps iterations to
 * MAX_ITERATIONS to guard against runaway loops on misconfigured
 * schedules; that ceiling is high enough (10_000) that no realistic
 * use case hits it (5 years of daily = 1825).
 */
function* iterateScheduleDates(schedule: AutoBuySchedule, today: Date): Generator<Date> {
  let next: Date | null;
  if (schedule.lastPopulatedDate) {
    next = stepForward(schedule.lastPopulatedDate, schedule.cadence, schedule.customMonthDays);
  } else {
    next = firstScheduledDate(schedule);
  }
  if (!next) return;

  const MAX_ITERATIONS = 10_000;
  let iterations = 0;

  while (next.getTime() <= today.getTime()) {
    if (schedule.endDate && next.getTime() > schedule.endDate.getTime()) return;
    yield new Date(next);
    iterations++;
    if (iterations >= MAX_ITERATIONS) {
      console.warn(`[auto-buy] schedule ${schedule.id} hit ${MAX_ITERATIONS} iterations; stopping`);
      return;
    }
    const stepped = stepForward(next, schedule.cadence, schedule.customMonthDays);
    if (!stepped) return;
    next = stepped;
  }
}

/**
 * Main walker. Pure function: takes schedules + price map + today,
 * returns the batch of new transactions and the schedule date updates.
 * Caller (store's runAutoBuyWalker action) merges into state in a
 * single withUndo block.
 *
 * @param schedules     The current list of auto-buy schedules.
 * @param priceMap      assetId -> current price USD (Decimal). For BTC
 *                      the key is 'btc'; for ETFs the key is holding.id.
 *                      Missing entries cause the schedule to be skipped
 *                      and lastPopulatedDate left unchanged.
 * @param today         The current Date. Passed as a param so tests can
 *                      pin it; in production this is new Date() from the
 *                      caller.
 */
export function walkAutoBuySchedules(
  schedules: AutoBuySchedule[],
  priceMap: Map<string, Decimal>,
  today: Date,
): AutoBuyWalkResult {
  const result: AutoBuyWalkResult = {
    newPurchases: [],
    newEtfTransactions: [],
    scheduleUpdates: [],
  };

  for (const schedule of schedules) {
    if (!schedule.enabled) continue;
    if (schedule.amountUsd.lessThanOrEqualTo(0)) continue;

    const price = priceMap.get(schedule.assetId);
    if (!price || price.lessThanOrEqualTo(0)) {
      // No price available. Skip this schedule's events this run; leave
      // lastPopulatedDate unchanged so we retry once prices arrive.
      continue;
    }

    let lastEmittedDate: Date | undefined;

    for (const date of iterateScheduleDates(schedule, today)) {
      if (schedule.assetType === 'btc') {
        // BTC accepts fractional satoshis; just divide.
        const btcAmount = schedule.amountUsd.div(price);
        const newTx: Omit<PurchaseTransaction, 'id'> = {
          date,
          usd: schedule.amountUsd as PurchaseTransaction['usd'],
          btc: btcAmount as PurchaseTransaction['btc'],
          category: 'manual',
          source: 'actual',
          autoCreated: true,
        };
        if (schedule.label) newTx.note = schedule.label;
        result.newPurchases.push(newTx);
      } else if (schedule.assetType === 'etf') {
        // ETF: integer shares only. floor(amount / price) shares; if
        // amount < price the period is skipped entirely.
        const sharesDec = schedule.amountUsd.div(price).floor();
        if (sharesDec.lessThanOrEqualTo(0)) {
          // Mark the date as "populated" anyway so we don't retry the
          // same too-small period on every walker run. The user can
          // increase the amount or change cadence to fix forward.
          lastEmittedDate = date;
          continue;
        }
        const newTx: Omit<EtfTransaction, 'id'> = {
          holdingId: schedule.assetId,
          date,
          shares: sharesDec,
          pricePerShareUsd: price,
          category: 'manual',
          autoCreated: true,
        };
        if (schedule.label) newTx.note = schedule.label;
        result.newEtfTransactions.push(newTx);
      } else {
        // Patch BJ: assetType === 'security'. Walker support for the
        // security ledger is added in Patch BK alongside the actual
        // sub-card UI. Until then we no-op (skip the iteration entirely)
        // rather than silently misroute the row into the ETF branch,
        // which would create wrong-typed rows. The schedule remains
        // intact; once BK ships, the walker picks up where it left off
        // and starts emitting SecurityTransactions on subsequent runs.
        break;
      }
      lastEmittedDate = date;
    }

    if (lastEmittedDate) {
      result.scheduleUpdates.push({ id: schedule.id, lastPopulatedDate: lastEmittedDate });
    }
  }

  return result;
}
